import { useStableCallback } from "@prismicio/editor-support/React"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import type {
  ID,
  SelectionManager,
  SelectionManagerProviderProps,
  SelectionManagerState,
  SelectionStateListener,
  ToggleSelectionArgs,
  UseSelectionManager,
} from "./SelectionManagerTypes"

const SelectionManagerContext = createContext<SelectionManager | undefined>(
  undefined,
)

const defaultState: SelectionManagerState = {
  selectedIds: new Set(),
  excludedIds: new Set(),
  isAllSelected: false,
}

export function SelectionManagerProvider(props: SelectionManagerProviderProps) {
  const {
    children,
    initialState,
    initialTotalItemCount,
    isSelectAllEnabled = false,
  } = props
  const lastSelectedIdRef = useRef<ID | undefined>()
  const totalItemCountRef = useRef<number>(initialTotalItemCount || 0)

  function setupInitialState() {
    if (initialState && initialState.selectedIds) {
      const lastSelectedId = [...initialState.selectedIds].pop()

      if (lastSelectedId) lastSelectedIdRef.current = lastSelectedId
    }

    return {
      ...defaultState,
      ...initialState,
    }
  }

  const [state, setState] = useState<SelectionManagerState>(setupInitialState)

  const selectedCount = useMemo(() => {
    const { selectedIds, excludedIds, isAllSelected } = state

    return isAllSelected
      ? totalItemCountRef.current - excludedIds.size
      : selectedIds.size
  }, [state])

  const isSelected = useCallback(
    (id: ID) => {
      const { isAllSelected, selectedIds, excludedIds } = state

      if (isAllSelected) {
        return !excludedIds.has(id)
      } else {
        return selectedIds.has(id)
      }
    },
    [state],
  )

  const getSelectionState = useCallback(() => {
    const { isAllSelected, selectedIds, excludedIds } = state
    return {
      selectedIds: [...selectedIds],
      excludedIds: [...excludedIds],
      isAllSelected,
      selectedCount,
    }
  }, [state, selectedCount])

  const selectAll = useStableCallback(() => {
    if (!isSelectAllEnabled) return

    setState({
      isAllSelected: true,
      selectedIds: new Set(),
      excludedIds: new Set(),
    })
  })

  const deselectAll = useStableCallback(() => {
    setState({
      isAllSelected: false,
      selectedIds: new Set(),
      excludedIds: new Set(),
    })
  })

  // Select / Delesect all when count reaches the totalItemCount
  useEffect(() => {
    const { isAllSelected, selectedIds, excludedIds } = state
    const totalItemCount = totalItemCountRef.current

    if (totalItemCount === 0 || !isSelectAllEnabled) return

    if (isAllSelected && excludedIds.size === totalItemCount) {
      deselectAll()
      return
    }

    if (!isAllSelected && selectedIds.size === totalItemCount) {
      selectAll()
      return
    }
  }, [state, isSelectAllEnabled, selectAll, deselectAll])

  const toggleSelection = useStableCallback((args: ToggleSelectionArgs) => {
    const { id, isSingleSelection, metaKey, shiftKey, ids } = args

    const isRangeSelection = shiftKey && ids && ids.length > 0

    if (isRangeSelection && lastSelectedIdRef.current) {
      toggleRangeSelection(id, ids, lastSelectedIdRef.current)
    } else if (isSingleSelection && !metaKey) {
      toggleSingleSelection(id)
    } else {
      // Handle multi selection
      isSelected(id)
        ? removeItemsFromSelection([id])
        : addItemsToSelection([id])
    }

    // Update the last selected id
    lastSelectedIdRef.current = id
    return isSelected(id)
  })

  function toggleSingleSelection(id: ID) {
    if (isSelected(id) && selectedCount === 1) {
      setState((prevState) => ({
        ...prevState,
        selectedIds: new Set(),
      }))
    } else {
      setState({
        isAllSelected: false,
        selectedIds: new Set([id]),
        excludedIds: new Set(),
      })
    }
  }

  function toggleRangeSelection(
    id: ID,
    ids: ID[],
    lastSelectedId: ID,
  ): boolean {
    // Get range from lastSelectedId to id
    const idsRange = getIdsRangeBetweenIds<ID>(ids, lastSelectedId, id)
    // Get id selection state
    const isIdSelected = isSelected(id)

    isIdSelected
      ? removeItemsFromSelection(idsRange)
      : addItemsToSelection(idsRange)

    return isIdSelected
  }

  const toggleSelectAll = useStableCallback(() => {
    selectedCount > 0 ? deselectAll() : selectAll()
  })

  function addItemsToSelection(ids: ID[]) {
    const { isAllSelected } = state
    if (isAllSelected) {
      setState((prevState) => ({
        ...prevState,
        excludedIds: deleteIdsFromSet(prevState.excludedIds, ids),
      }))
      return
    }

    setState((prevState) => ({
      ...prevState,
      selectedIds: addIdsToSet(prevState.selectedIds, ids),
    }))
  }

  function removeItemsFromSelection(ids: ID[]) {
    const { isAllSelected } = state
    if (isAllSelected) {
      setState((prevState) => ({
        ...prevState,
        excludedIds: addIdsToSet(prevState.excludedIds, ids),
      }))
      return
    }

    setState((prevState) => ({
      ...prevState,
      selectedIds: deleteIdsFromSet(prevState.selectedIds, ids),
    }))
  }

  function setTotalItemCount(value: number) {
    totalItemCountRef.current = value
  }

  const context = useMemo(
    () => ({
      selectedCount,
      state,
      totalItemCount: totalItemCountRef.current,
      isSelected,
      isSelectAllEnabled,

      getSelectionState,
      toggleSelection,
      toggleSelectAll,
      clearSelection: deselectAll,
      setTotalItemCount,
    }),
    [
      state,
      deselectAll,
      getSelectionState,
      isSelectAllEnabled,
      isSelected,
      selectedCount,
      toggleSelectAll,
      toggleSelection,
    ],
  )

  return (
    <SelectionManagerContext.Provider value={context}>
      {children}
    </SelectionManagerContext.Provider>
  )
}

interface useSelectionManagerArgs {
  totalItemCount?: number
  onChangeSelection?: SelectionStateListener
}

export function useSelectionManager(
  args?: useSelectionManagerArgs,
): UseSelectionManager {
  const context = useContext(SelectionManagerContext)

  if (context === undefined) {
    throw new Error(
      "useSelectionManagerContext must be used within a SelectionManagerProvider",
    )
  }
  const { totalItemCount: maybeUpdatedTotalItemCount, onChangeSelection } =
    args || {}

  const {
    setTotalItemCount,
    totalItemCount,
    state,
    getSelectionState,
    ...remainingContext
  } = context

  // Synchronize the total item count
  if (
    maybeUpdatedTotalItemCount !== undefined &&
    maybeUpdatedTotalItemCount !== totalItemCount
  ) {
    setTotalItemCount(maybeUpdatedTotalItemCount)
  }

  const stableOnChangeSelection = useStableCallback(onChangeSelection)
  useEffect(() => {
    stableOnChangeSelection?.(getSelectionState())
  }, [getSelectionState, stableOnChangeSelection, state])

  return {
    ...remainingContext,
    getSelectionState,
    isAllSelected: state.isAllSelected,
  }
}

function getIdsRangeBetweenIds<T>(idsArray: T[], pivotId: T, targetId: T): T[] {
  const pivotIndex = idsArray.indexOf(pivotId)
  const targetIndex = idsArray.indexOf(targetId)

  if (targetIndex === -1) {
    throw new Error("targetId must be in the array")
  }

  // If pivotId is not in the array, single select targetId
  if (pivotIndex === -1) {
    return [targetId]
  }

  if (pivotIndex <= targetIndex) {
    return idsArray.slice(pivotIndex, targetIndex + 1)
  } else {
    return idsArray.slice(targetIndex, pivotIndex + 1)
  }
}

function deleteIdsFromSet(prevSet: Set<ID>, ids: ID[]): Set<ID> {
  const nextSet = new Set(prevSet)
  ids.forEach((id) => nextSet.delete(id))
  return nextSet
}

function addIdsToSet(prevSet: Set<ID>, ids: ID[]): Set<ID> {
  return new Set([...prevSet, ...ids])
}
